import type {
  BrandSpecials,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import { Flex, Typography } from '@iheartjane/reefer';
import type { LabeledOption, Nullish } from '@iheartjane/types';
import { useMemo } from 'react';

import { MobileFiltersModal } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { FilterSelector } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/filterSelector';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  ProductApplicableIds,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { SortOptions } from '@jane/bloom/types/sort';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';

import styles from './goldProductGrid.module.css';

type GoldProductGridProps = {
  applicablePriceIds?: Nullish<ProductApplicableIds[]>;
  brandSpecials: BrandSpecials;
  currentSort: SortOptions;
  customLanguage: CustomLanguage;
  janeGold: JaneGold;
  preloadedProductIds: number[];
  rowInstance: SmartSort<MaterializedMenuProduct> | undefined;
  sortOptions: LabeledOption<SortOptions>[];
  storeSpecials: StoreSpecials;
};

export const GoldProductGrid = ({
  applicablePriceIds,
  brandSpecials,
  currentSort,
  customLanguage,
  janeGold,
  preloadedProductIds,
  rowInstance,
  sortOptions,
  storeSpecials,
}: GoldProductGridProps) => {
  const { featureFlags } = useRootLoaderData();
  const totalProductCount = useMemo(() => {
    if (!rowInstance) {
      return 0;
    }

    return rowInstance.totalProducts;
  }, [rowInstance]);

  const hasResults = useMemo(() => {
    if (!rowInstance) {
      return false;
    }

    return rowInstance.products.length > 0;
  }, [rowInstance]);

  if (!rowInstance) {
    return <></>;
  }

  return (
    <div>
      <FilterSelector
        availableProductsCount={rowInstance?.totalProducts}
        sortOptions={sortOptions}
        currentSort={currentSort}
        hideLabeledFilters={true}
        hideSearch
        showCompare={featureFlags[FLAGS.c1CompareProducts]}
      />

      <div className={styles['goldMobileFilters']}>
        <Typography color="inherit">
          {`${totalProductCount > 999 ? '1k+' : totalProductCount} products`}
        </Typography>
        <MobileFiltersModal
          availableProductsCount={totalProductCount}
          sortOptions={sortOptions}
          currentSort={currentSort}
        />
      </div>

      {!hasResults && (
        <Flex justifyContent="center" alignItems="center" height="500px">
          <Typography
            color="inherit"
            variant="h1-semibold"
            data-testid="no-results"
          >
            No Products Available
          </Typography>
        </Flex>
      )}

      <ProductGrid
        brandSpecials={brandSpecials}
        customLanguage={customLanguage}
        preloadedProductIds={preloadedProductIds}
        rowInstance={rowInstance}
        //TODO: this doesn't seem to apply for product brand specials, but will be necessary for the other brand special types
        // weightFilter={shouldShowWeight({
        //   cartItems: cart?.items || [],
        //   currentSpecial,
        //   specialsProducts,
        // })}
        janeGold={janeGold}
        applicablePriceIds={applicablePriceIds}
        specials={storeSpecials}
      />
    </div>
  );
};
